import { useVirtualizer } from '@tanstack/react-virtual'
import { useRef, useState } from 'react'
import type { DragEvent, ReactNode } from 'react'

import { VIRTUALISE_ABOVE } from '@/components/data/DataTable'
import { cn } from '@/lib/cn'

/* The kanban form of a record list — the same records the table shows, grouped
 * into one lane per status so the PIPELINE is what the eye reads, not the row.
 *
 * The board is a second VIEW of the list, never a second source: it takes the
 * same data array the table gets and groups it client-side, so the two views
 * can never disagree about what exists.
 *
 * Dragging a card to another lane is a real status change (EXPERIENCE.md
 * § Board card): a pill is a label, a column is a location, and moving
 * something to a new location is an understood act of intent. The board owns
 * only the GESTURE — which lanes light up, which drops land — through two
 * optional props: `canMoveTo` says where a card may go, `onMoveCard` receives
 * the drop. What a drop MEANS (complete with undo, open the record, refuse) is
 * the surface's policy, decided in the page; the board must not know that
 * sending a quote issues a PDF. Neither prop given — the cases board today —
 * and cards simply are not draggable.
 *
 * Drag is a pointer shortcut, not the only path: every transition it performs
 * is also reachable from the record's own page, which is where the keyboard
 * and screen-reader path lives. That is why the desktop board alone drags —
 * on the phone the lanes stack as headed groups and the record page does the
 * moving.
 *
 * Below the table breakpoint the lanes stack vertically as headed groups —
 * the same "change the pattern, never shrink" rule RecordList states: a
 * horizontally scrolling board on a phone is a horizontally scrolling grid
 * with rounded corners. Empty lanes disappear on the phone; on desktop they
 * stay, because an empty lane in a pipeline is information — and given
 * `laneEmpty` they say so in words, rather than leaving a headed rectangle the
 * reader has to read as either "none" or "not loaded".
 *
 * A lane carries the tint of the status it holds (`tone`, the same five tones
 * StatusPill uses), so the shape of the pipeline is legible before a single
 * card is. See LANE_TINT for why the fill is weaker than the pill's.
 *
 * The table/board choice itself lives in useViewState, one file over.
 *
 * VIRTUALISATION IS PER LANE, not across the board (UX-DR-4, and the decision
 * that chose @tanstack/react-virtual for it). Lanes scroll independently —
 * that is what a lane IS — so one window spanning the board would have to
 * answer "which lane's scroll position?" and there is no answer. A lane windows
 * only once it alone passes DataTable's threshold, so a board of four lanes
 * holding thirty cards each renders every card, exactly as it does today.
 * Cards are of caller-supplied height, so unlike the table's fixed rows they
 * are measured after paint and the estimate below only has to be close.
 *
 * The phone stack is not windowed, for the reason RecordList gives about its
 * own card list: windowing it means a scroll region nested inside the page
 * scroll, which is the one shape a phone handles worst.
 */

/** Roughly a two-line card with its padding. Only the first paint uses it —
 *  `measureElement` replaces it with the real height as each card mounts. */
const CARD_ESTIMATE_PX = 72

/** gap-2.5 between the desktop lane's cards, as a number the virtualiser can
 *  add to its offsets. The windowed lane positions its cards absolutely, so
 *  the flex gap is no longer there to do it. */
const CARD_GAP_PX = 10

/** One desktop lane's card list.
 *
 *  Its own component purely so it can hold a virtualiser: a lane is rendered
 *  inside a `map`, and a hook cannot be called there. Below the threshold this
 *  renders the same flex `<ul>` the lane always rendered.
 *
 *  Keeping it small has a second payoff. `useVirtualizer` returns functions the
 *  React Compiler will not memoise across, so it skips whatever component holds
 *  it (react-hooks/incompatible-library) — and what it skips here is one lane's
 *  card list, not RecordBoard, whose grouping, drag state and lane headers are
 *  compiled as before. Anything moved into this function loses that. */
function LaneCards<TData>({
  rows,
  getRowId,
  renderCard,
}: {
  rows: TData[]
  getRowId: (row: TData) => string
  renderCard: (row: TData) => ReactNode
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualise = rows.length > VIRTUALISE_ABOVE

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => CARD_ESTIMATE_PX,
    gap: CARD_GAP_PX,
    overscan: 8,
    enabled: virtualise,
  })

  if (!virtualise) {
    return (
      <ul className="flex min-h-9 flex-col gap-2.5">
        {rows.map((row) => (
          <li key={getRowId(row)}>{renderCard(row)}</li>
        ))}
      </ul>
    )
  }

  /* The scroll box is a `<div>` AROUND the `<ul>` rather than the `<ul>`
     itself. The box's height is the viewport — that is what makes it scroll —
     so the full measured height of the list has to live on something inside
     it, or the scrollbar reports the size of the window instead of the size of
     the lane. A `<ul>` may only contain `<li>`, which is why the sizer is the
     list and not a wrapper within it. Its cards are then positioned absolutely
     against it, and that is also why the flex gap is gone: the virtualiser's
     `gap` option carries the spacing in the offsets instead. */
  return (
    <div ref={scrollRef} className="asa-scroll max-h-[70vh] min-h-9 overflow-y-auto">
      <ul className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((item) => {
          const row = rows[item.index]
          if (!row) return null
          return (
            <li
              key={getRowId(row)}
              // data-index and the ref are how the virtualiser finds a mounted
              // card and records what it actually measured.
              data-index={item.index}
              ref={virtualizer.measureElement}
              className="absolute top-0 left-0 w-full"
              style={{ transform: `translateY(${item.start}px)` }}
            >
              {renderCard(row)}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** The lane's tint, named after the status tone it belongs to.
 *
 *  The same five tones StatusPill uses, so a lane and the pill heading it carry
 *  one meaning between them rather than two colour systems that happen to agree
 *  today. The tint is deliberately weaker than the pill's: a pill is a shape the
 *  size of a word, a lane is a column the height of the screen, and the same
 *  fill at that size stops being a hint and becomes a background the cards have
 *  to fight. Colour still carries nothing alone — the pill in the heading names
 *  the lane in words. */
export type LaneTone = 'neutral' | 'brand' | 'positive' | 'negative' | 'attention'

const LANE_TINT: Record<LaneTone, string> = {
  neutral: 'bg-neutral-50',
  brand: 'bg-brand-soft/40',
  positive: 'bg-positive-soft/50',
  negative: 'bg-negative-soft/50',
  attention: 'bg-attention-soft/60',
}

export interface BoardLane<S extends string> {
  value: S
  /** The lane heading — normally the matching status pill. */
  header: ReactNode
  /** The lane's tint. Defaults to the neutral grey every lane used to be. */
  tone?: LaneTone
}

export interface RecordBoardProps<TData, S extends string> {
  data: TData[]
  lanes: readonly BoardLane<S>[]
  groupBy: (row: TData) => S
  /** The card form of one record. Status is implied by the lane — leave it off. */
  renderCard: (row: TData) => ReactNode
  getRowId: (row: TData) => string
  onCardClick?: (row: TData) => void
  /** Whether a card may be DROPPED on a lane. Consulted while dragging: a
   *  refused lane never lights up and never receives the drop, so "this move
   *  is not a thing" is said by the gesture, not by an error after it. */
  canMoveTo?: (row: TData, lane: S) => boolean
  /** A completed drop on a permitted lane other than the card's own. The
   *  surface decides what the move MEANS; the board only reports it. */
  onMoveCard?: (row: TData, lane: S) => void
  /** What a lane is WORTH, rendered at the right of its heading. A pipeline is
   *  read by weight as much as by count, and the count alone does not say
   *  whether the sent lane holds two small quotes or the quarter. Optional
   *  because only the surface knows which of its figures sums meaningfully. */
  laneTotal?: (rows: TData[]) => ReactNode
  /** What an EMPTY lane says, on the desktop board where empty lanes stay.
   *
   *  Per lane rather than one shared node, because the honest sentence differs:
   *  a lane that accepts drops can invite one, and a lane nothing may be dragged
   *  into must not — an instruction that does not work where it is printed is
   *  worse than the blank space it filled. Left off, an empty lane stays blank
   *  exactly as it did. */
  laneEmpty?: (lane: S) => ReactNode
  empty?: ReactNode
  label: string
}

export function RecordBoard<TData, S extends string>({
  data,
  lanes,
  groupBy,
  renderCard,
  getRowId,
  onCardClick,
  canMoveTo,
  onMoveCard,
  laneTotal,
  laneEmpty,
  empty,
  label,
}: RecordBoardProps<TData, S>) {
  /* The row in flight, held as STATE rather than read from dataTransfer:
     dataTransfer is write-only during dragover in every engine, so the lane
     could not consult canMoveTo from it — and the drag never leaves this
     board, so nothing is lost by keeping identity local. setData still runs
     with a dummy payload because Firefox refuses to start a drag without one. */
  const [dragging, setDragging] = useState<TData | null>(null)
  const [hovered, setHovered] = useState<S | null>(null)

  if (data.length === 0) return <>{empty}</>

  const groups = new Map<S, TData[]>(lanes.map((lane) => [lane.value, []]))
  for (const row of data) groups.get(groupBy(row))?.push(row)

  const draggable = Boolean(onMoveCard)

  const acceptsDrop = (lane: S): boolean =>
    dragging !== null &&
    groupBy(dragging) !== lane &&
    (canMoveTo ? canMoveTo(dragging, lane) : true)

  const card = (row: TData) => (
    <button
      type="button"
      onClick={onCardClick ? () => onCardClick(row) : undefined}
      draggable={draggable || undefined}
      onDragStart={
        draggable
          ? (event: DragEvent) => {
              event.dataTransfer.effectAllowed = 'move'
              event.dataTransfer.setData('text/plain', getRowId(row))
              setDragging(row)
            }
          : undefined
      }
      onDragEnd={
        draggable
          ? () => {
              setDragging(null)
              setHovered(null)
            }
          : undefined
      }
      className={cn(
        'bg-surface border-border-subtle w-full rounded-[var(--radius-DEFAULT)]',
        'border px-3.5 py-3 text-left shadow-xs',
        'hover:bg-surface-hover transition-colors',
        'duration-[var(--dur-instant)] ease-[var(--ease-standard)]',
        onCardClick ? 'cursor-pointer' : 'cursor-default',
        draggable && 'cursor-grab active:cursor-grabbing',
        // The card in flight ghosts where it came from, so the eye tracks the
        // copy under the pointer rather than seeing the record twice.
        dragging !== null && getRowId(dragging) === getRowId(row) && 'opacity-40',
      )}
    >
      {renderCard(row)}
    </button>
  )

  /* Heading: what the lane IS on the left, what it is WORTH on the right. The
     count sits against the label rather than against the total, so the pair
     reads as one phrase ("SENT 2") and the money keeps the right edge to
     itself — the same alignment the cards below give their figures. */
  const laneHeader = (lane: BoardLane<S>, rows: TData[]) => (
    <header className="flex items-baseline gap-2 px-1.5">
      <span className="flex min-w-0 items-baseline gap-1.5">
        {lane.header}
        <span className="asa-num text-text-faint text-[11px]">{rows.length}</span>
      </span>
      <span className="flex-1" />
      {laneTotal ? (
        <span className="asa-num text-text-muted shrink-0 text-[11px]">
          {laneTotal(rows)}
        </span>
      ) : null}
    </header>
  )

  return (
    <div role="group" aria-label={label}>
      {/* Desktop: one lane per status, side by side. The board scrolls inside
          its own container when lanes outgrow the card — same rule that keeps
          a dense table from widening the page. */}
      <div className="asa-scroll hidden gap-2.5 overflow-x-auto p-3 md:flex">
        {lanes.map((lane) => {
          const rows = groups.get(lane.value) ?? []
          const accepting = acceptsDrop(lane.value)
          return (
            <section
              key={lane.value}
              data-lane={lane.value}
              onDragOver={
                draggable
                  ? (event: DragEvent) => {
                      // preventDefault is the DOM's "yes, droppable here". A
                      // refused lane skips it, so the browser shows the
                      // not-allowed cursor and the drop can never land.
                      if (!accepting) return
                      event.preventDefault()
                      event.dataTransfer.dropEffect = 'move'
                      setHovered(lane.value)
                    }
                  : undefined
              }
              onDragLeave={
                draggable
                  ? () => setHovered((current) => (current === lane.value ? null : current))
                  : undefined
              }
              onDrop={
                draggable
                  ? (event: DragEvent) => {
                      if (!accepting || dragging === null) return
                      event.preventDefault()
                      const moved = dragging
                      setDragging(null)
                      setHovered(null)
                      onMoveCard?.(moved, lane.value)
                    }
                  : undefined
              }
              className={cn(
                /* A floor, not a fixed width: lanes share whatever the card is
                   wide enough to give them, and only start scrolling once the
                   pipeline no longer fits at the minimum. A fixed 288 both left
                   a band of empty card beside a short board and scrolled a
                   six-lane one that had the room for it.

                   The floor is 256 because that is what the CARD needs: below
                   it the quote card's identity line — number, revision, date —
                   stops fitting on one row and the number wraps, and a lane
                   narrow enough to break the thing being read is not a lane
                   that fits. Six lanes at 256 overrun the 1440 page cap by
                   about a scrollbar's worth, and scrolling is the documented
                   answer to a pipeline wider than its card. */
                'flex min-w-[256px] flex-1 flex-col gap-2.5',
                'rounded-[var(--radius-lg)] p-2.5',
                LANE_TINT[lane.tone ?? 'neutral'],
                // The invitation: only a lane that would ACCEPT the drop
                // lights up. Brand ring rather than a fill, so the cards
                // inside stay readable under the drag.
                accepting && hovered === lane.value && 'ring-brand ring-2',
                accepting && hovered !== lane.value && 'ring-border-default ring-1',
              )}
            >
              {laneHeader(lane, rows)}
              {rows.length === 0 && laneEmpty ? (
                <div className="px-1.5 pt-1 pb-3">{laneEmpty(lane.value)}</div>
              ) : (
                <LaneCards rows={rows} getRowId={getRowId} renderCard={card} />
              )}
            </section>
          )
        })}
      </div>

      {/* Phone: the lanes stack as headed groups. Empty lanes are dropped —
          a heading over nothing is noise on a screen this small. No drag here:
          the record page is the phone's way of moving one. */}
      <div className="flex flex-col gap-4 p-3 md:hidden">
        {lanes.map((lane) => {
          const rows = groups.get(lane.value) ?? []
          if (rows.length === 0) return null
          return (
            <section key={lane.value} className="flex flex-col gap-2">
              {laneHeader(lane, rows)}
              <ul className="flex flex-col gap-2">
                {rows.map((row) => (
                  <li key={getRowId(row)}>{card(row)}</li>
                ))}
              </ul>
            </section>
          )
        })}
      </div>
    </div>
  )
}
